import { useMutation, UseMutationOptions, useQueryClient } from '@tanstack/react-query';
import { InitiatePaymentResponse, PortalNotification } from '../types';
import { CreateAccessTokenRequest, CreateAccessTokenResponse, GetDownloadRequest, GetDownloadResponse, InitiatePaymentRequest, MergeDownloadRequest, MergeDownloadResponse, SubmitPayloadPaymentResponse, UpdateSmsPreferenceStatusRequest, UpdateSmsPreferenceStatusResponse } from '../types/portalTypes';
import { GetUnauthedDownloadResponse, GetUnauthedSigningResponse } from '../types/portalTypes';
import { SubmitAccountVerificationRequest, SubmitAccountVerificationResponse } from '../types/portalTypes/api/SubmitAccountVerification';
import { apiRoutes } from './apiRoutes';
import { InitiatePayloadPaymentResponse, SubmitPayloadPaymentRequest } from '../types/portalTypes/api/InitiatePayloadPayment';
import { CsError } from './CsError';
import { usePinTokenState } from '../components/context/pin-state/PinState';
import { useUnauthedApiMutation } from './useUnauthedApiMutation';
import { useAuth0 } from '@auth0/auth0-react';
import { useOrganization } from '../components/OrganizationProvider';
import { getScopes, getAudience } from './auth0Utils';
import { pinMutationFunction } from './apiFunctions/mutationFunctions/pinMutationFunction';
import { auth0MutationFunction } from './apiFunctions/mutationFunctions/auth0MutationFunction';
import { useIsPinPage } from './useIsPinPage';
import { useOptimisticUpdateGetConfig } from './optimisticUpdates';
import { OrderRequestsUploadUrlBody, OrderRequestsUploadUrlResponse, SubmitOrderRequestBody, SubmitOrderRequestResponse } from '../types/orderRequest';

type ApiQueryKey = readonly [string]
type ApiMutationOptions<TResponse, TRequestBody = undefined> = Omit<UseMutationOptions<TResponse, CsError, TRequestBody>, 'mutationFn'>

export function useInitiatePayment(orderKey: string, notificationId: string, search: string, queryOptions?: ApiMutationOptions<InitiatePaymentResponse, InitiatePaymentRequest>) {
  return useApiMutation<InitiatePaymentResponse, InitiatePaymentRequest>(
    apiRoutes.initiatePayment(orderKey, notificationId, search),
    'POST',
    queryOptions
  )
}

export function useInitiatePayloadPayment(orderKey: string, notificationId: string, search: string, queryOptions?: ApiMutationOptions<InitiatePayloadPaymentResponse>) {
  return useApiMutation<InitiatePayloadPaymentResponse, undefined>(
    apiRoutes.initiatePayloadPayment(orderKey, notificationId, search),
    'POST',
    queryOptions
  )
}

export function useSubmitPayloadPayment(orderKey: string, notificationId: string, search: string, queryOptions?: ApiMutationOptions<SubmitPayloadPaymentResponse, SubmitPayloadPaymentRequest>) {
  return useApiMutation<SubmitPayloadPaymentResponse, SubmitPayloadPaymentRequest>(
    apiRoutes.submitPayloadPayment(orderKey, notificationId, search),
    'POST',
    queryOptions
  )
}

//TODO: the PortalNotification type is wrong, the only reason this works is because we are checking if it has an ok response, not what is in it.
//  Should look at portal-api to figure out the correct type
export function usePaymentSubmit(orderKey: string, notificationId: string, search: string, queryOptions?: ApiMutationOptions<PortalNotification>) {
  return useApiMutation<PortalNotification>(apiRoutes.paymentSubmit(orderKey, notificationId, search), 'POST', queryOptions);
}

export function useSubmitAccountVerification(orderKey: string, notificationKey: string, search: string, queryOptions?: ApiMutationOptions<SubmitAccountVerificationResponse, SubmitAccountVerificationRequest>) {
  return useApiMutation<SubmitAccountVerificationResponse, SubmitAccountVerificationRequest>(
    apiRoutes.submitAccountVerification(orderKey, notificationKey, search),
    'POST',
    queryOptions
  )
}

export function useCreateAccessToken(orderKey: string, notificationId: string, search: string, queryOptions?: ApiMutationOptions<CreateAccessTokenResponse, CreateAccessTokenRequest>) {
  return useApiMutation<CreateAccessTokenResponse, CreateAccessTokenRequest>(
    apiRoutes.createAccessToken(orderKey, notificationId, search),
    'POST',
    queryOptions
  );
}

export function useDeclinePlaid(orderKey: string, notificationId: string, search: string, queryOptions?: ApiMutationOptions<{ statusCode: string }>) {
  return useApiMutation<{ statusCode: string }, undefined>(
    apiRoutes.declinePlaid(orderKey, notificationId, search),
    'POST',
    queryOptions
  )
}

export function useUserResetTask(orderKey: string, notificationId: string, search: string, queryOptions?: ApiMutationOptions<any>) {
  return useApiMutation<any>(
    apiRoutes.userResetTask(orderKey, notificationId, search),
    'POST',
    queryOptions
  );
}

export function useUserCancelTask(orderKey: string, notificationId: string, search: string, queryOptions?: ApiMutationOptions<any>) {
  return useApiMutation<any>(
    apiRoutes.userCancelTask(orderKey, notificationId, search),
    'POST',
    queryOptions
  );
}

export function useUserCompleteTask(orderKey: string, notificationId: string, search: string, queryOptions?: ApiMutationOptions<any>) {
  return useApiMutation<any>(
    apiRoutes.userCompleteTask(orderKey, notificationId, search),
    'POST',
    queryOptions
  );
}

export function useUserSigned(orderKey: string, notificationId: string, search: string, queryOptions?: ApiMutationOptions<any>) {
  return useApiMutation<any>(
    apiRoutes.userSigned(orderKey, notificationId, search),
    'POST',
    queryOptions
  );
}


export function useUpdateSmsPreferenceStatus(search: string, queryOptions?: ApiMutationOptions<UpdateSmsPreferenceStatusResponse, UpdateSmsPreferenceStatusRequest>
) {
  return useApiMutation<
    UpdateSmsPreferenceStatusResponse,
    UpdateSmsPreferenceStatusRequest
  >(
    apiRoutes.communicationPreference(search),
    'POST',
    queryOptions
  );
}

//TODO: the PortalNotification type is wrong, the only reason this works is because we are checking if it has an ok response, not what is in it.
//  Should look at portal-api to figure out the correct type
export function useFormSubmit(
  orderKey: string,
  formKey: string,
  search: string,
  queryOptions?: ApiMutationOptions<PortalNotification, any>
) {
  return useApiMutation<PortalNotification, any>(
    `order/${orderKey}/form/${formKey}/submit${search}`,
    'POST',
    queryOptions
  );
}

export function useFormSave(
  orderKey: string,
  notificationId: string,
  formKey: string,
  search: string,
  queryOptions?: ApiMutationOptions<any, any> 
) {
  return useApiMutation<any, any>(
    `order/${orderKey}/notification/${notificationId}/form/${formKey}/save${search}`,
    'POST',
    queryOptions
  );
}

export function useGetUnauthedSigning(
  pageUrl: string,
  hash: string,
  queryOptions?: ApiMutationOptions<GetUnauthedSigningResponse, { Pin: string }>
) {
  return useUnauthedApiMutation<GetUnauthedSigningResponse, { Pin: string }>(
    apiRoutes.getUnauthedSigning(pageUrl, hash),
    'POST',
    queryOptions
  )
}

export function useGetUnauthedDownload(
  pageUrl: string,
  hash: string,
  queryOptions?: ApiMutationOptions<GetUnauthedDownloadResponse, { Pin: string }>,
) {
  return useUnauthedApiMutation<GetUnauthedDownloadResponse, { Pin: string }>(
    apiRoutes.getUnauthedDownload(pageUrl, hash),
    'POST',
    queryOptions
  )
}


export function useVerifyPin(
  orderKey: string,
  notificationId: string,
  userOrderKey: string,
  queryOptions?: ApiMutationOptions<{ token: string }, { Pin: string }>
) {
  const { setPinToken } = usePinTokenState()

  const { optimisticUpdateGetConfig } = useOptimisticUpdateGetConfig()

  return useUnauthedApiMutation<{ token: string }, { Pin: string }>(
    apiRoutes.verifyPin(orderKey, notificationId, userOrderKey),
    'POST',
    {
      ...queryOptions,
      onSuccess: (data, variables, context) => { 
        setPinToken(data.token)
        optimisticUpdateGetConfig()
        queryOptions?.onSuccess?.(data, variables, context)
      }
    }
  )
}

export function useOrderRequestsUploadUrl(
  queryOptions?: ApiMutationOptions<OrderRequestsUploadUrlResponse, OrderRequestsUploadUrlBody>
) {
  return useApiMutation<OrderRequestsUploadUrlResponse, OrderRequestsUploadUrlBody>(
    apiRoutes.orderRequestsUploadUrl(),
    'POST',
    queryOptions
  )
}

export function useUnauthedOrderRequestsUploadUrl(
  queryOptions?: ApiMutationOptions<OrderRequestsUploadUrlResponse, OrderRequestsUploadUrlBody>
) {
  return useUnauthedApiMutation<OrderRequestsUploadUrlResponse, OrderRequestsUploadUrlBody>(
    apiRoutes.unauthedOrderRequestsUploadUrl(),
    'POST',
    queryOptions
  )
}

export function useSubmitOrderRequest(
  queryOptions?: ApiMutationOptions<SubmitOrderRequestResponse, SubmitOrderRequestBody>
) {
  return useApiMutation<SubmitOrderRequestResponse, SubmitOrderRequestBody>(
    apiRoutes.submitOrderRequest(),
    'POST',
    queryOptions
  )
}

export function useUnauthedSubmitOrderRequest(
  queryOptions?: ApiMutationOptions<SubmitOrderRequestResponse, SubmitOrderRequestBody>
) {
  return useUnauthedApiMutation<SubmitOrderRequestResponse, SubmitOrderRequestBody>(
    apiRoutes.unauthedSubmitOrderRequest(),
    'POST',
    queryOptions
  )
}

export function useCancelOrderRequest(requestId: string) {
  return useApiMutation<{ success: boolean }>(apiRoutes.cancelOrderRequest(requestId), 'DELETE')
}

export type ReplyNotificationRequest = { message: string; documentFileName?: string; originalDocumentFileName?: string }

export function useReplyNotification(orderKey: string, notificationKey: string, search: string, queryOptions?: ApiMutationOptions<void, ReplyNotificationRequest>) {
  return useApiMutation<void, ReplyNotificationRequest>(
    apiRoutes.replyNotification(orderKey, notificationKey, search),
    'POST',
    queryOptions
  )
}

export function useMarkReplyRead(orderKey: string, notificationKey: string, search: string, queryOptions?: ApiMutationOptions<void, undefined>) {
  return useApiMutation<void, undefined>(
    apiRoutes.replyRead(orderKey, notificationKey, search),
    'POST',
    queryOptions
  )
}

export function useGetDownloadLink({ CompanyIdOrderIdHash, search }: {
  CompanyIdOrderIdHash: string;
  search: string;
}) {
  return useApiMutation<GetDownloadResponse, GetDownloadRequest>(`order/${CompanyIdOrderIdHash }/download${search}`, 'POST')
}

export function useApiMutation<TResponse, TRequestBody = undefined>(
  path: string,
  method: 'POST' | 'PUT' | 'DELETE',
  mutationOptions?: ApiMutationOptions<TResponse, TRequestBody>
) {
  const isPinPage = useIsPinPage()

  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const organization = useOrganization();
  const scope = getScopes(organization?.MfaRequired);
  const audience = getAudience(organization?.MfaRequired);

  const mutationKey = [path] as ApiQueryKey
  const queryClient = useQueryClient()
  const { pinToken } = usePinTokenState()

  return useMutation<TResponse, CsError, TRequestBody>({
    onSuccess: (data) => {
      // Cache the result so it can be reused
      queryClient.setQueryData(mutationKey, data);
    },
    ...mutationOptions,
    useErrorBoundary: (error) => {

      if (error.cause === 'no_pin_token') {
        return true
      }

      const fallback = false

      // If user provided a useErrorBoundary option
      if (typeof mutationOptions?.useErrorBoundary === 'function') {
        return mutationOptions.useErrorBoundary(error)
      }
      if (typeof mutationOptions?.useErrorBoundary === 'boolean') {
        return mutationOptions.useErrorBoundary
      }
      return fallback
    },
    mutationKey,
    mutationFn: async (body: TRequestBody) => {
      if (isPinPage) {
        return pinMutationFunction<TResponse, TRequestBody>(body, method, path, pinToken);
      } else {
        return auth0MutationFunction<TResponse, TRequestBody>(body, method, path, getAccessTokenSilently, loginWithRedirect, audience, scope)
      }
    }
  })
}

export function useMergeDownloadLink({ CompanyIdOrderIdHash, search }: {
  CompanyIdOrderIdHash: string;
  search: string;
}) {
  return useApiMutation<MergeDownloadResponse, MergeDownloadRequest>(apiRoutes.mergeDownload(CompanyIdOrderIdHash, search), 'POST')
}
